import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Paper, IconButton, Tooltip, Divider, Rating } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";

import { 
  Visibility as VisibilityIcon, 
  CloudUpload as UploadIcon, 
  ArrowBack as BackIcon,
  Description as DocIcon,
  AccessTime as TimeIcon,
  Person as UserIcon,
  Category as ServiceIcon,
  Send as SendIcon,
  VideoCall as VideoIcon,
  Star as StarIcon,
  Delete as DeleteIcon,
  Chat as ChatIcon,
  AttachFile as AttachFileIcon,
  Work as WorkIcon,
  Payment as PaymentIcon,
  CheckCircle as CheckCircleIcon,
  WarningAmber as WarningIcon,
  ReceiptLong as ReceiptIcon,
  PhoneInTalk as PhoneIcon
} from "@mui/icons-material";
import axiosInstance from "../../../../api/axiosInstance";
import { motion, AnimatePresence } from "framer-motion";
import { initializeSocket } from "../../../../utils/socket";

const API_BASE_URL = window.location.hostname === "localhost" 
  ? "http://localhost:5030" 
  : "https://backend.webbirth.online:5030";

const getFullUrl = (url) => {
  if (!url || url.includes("undefined") || url === "#") return "#";
  if (url.startsWith("http")) return url;
  if (url.includes("uploads") && (url.includes(":\\") || url.includes("C:"))) {
    const fileName = url.split(/[\\/]/).pop(); 
    return `${API_BASE_URL}/uploads/${fileName}`;
  }
  const cleanPath = url.startsWith("/") ? url : `/${url}`;
  return `${API_BASE_URL}${cleanPath}`;
};

const formatDisplayDate = (dateString) => {
  if (!dateString) return "—";
  try {
    const date = new Date(dateString);
    return date.toLocaleString("en-GB", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: true,
    });
  } catch { return "Invalid Date"; }
};

const NoteWithReadMore = ({ note }) => {
  const [expanded, setExpanded] = useState(false);
  if (!note) return null;
  const isLong = note.length > 50;
  const displayText = !expanded && isLong ? note.slice(0, 50) + "..." : note;

  return (
    <div className="mt-1 text-[10px] sm:text-[11px] text-slate-600 bg-slate-50 border border-slate-100 p-2 rounded-lg max-w-md whitespace-normal break-words leading-relaxed">
      <span className="italic">{displayText}</span>
      {isLong && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
          className="ml-2 text-blue-600 font-bold not-italic hover:underline cursor-pointer text-[11px]"
        >
          {expanded ? "Read Less" : "Read More"}
        </button>
      )}
    </div>
  );
};

const getColumns = (activeTab, onDeleteDoc) => [
  {
    field: "documentName",
    headerName: "Document / Note Details",
    flex: 1,
    minWidth: 350,
    renderCell: (params) => (
      <div className="flex flex-col justify-center h-full py-3 space-y-1 w-full">
        <span className="font-bold text-slate-800 text-sm flex items-start gap-2 whitespace-normal break-words leading-tight">
          <DocIcon sx={{ fontSize: 16, color: "#134DA7", mt: 0.25 }} />
          <span>{params.value || "Document"}</span>
        </span>
        <NoteWithReadMore note={params.row.notes || params.row.note || params.row.message || params.row.userNotes || params.row.description} />
      </div>
    ),
  },
  {
    field: "createdAt",
    headerName: "Date Uploaded",
    width: 160,
    renderCell: (params) => (
      <span className="text-xs font-medium text-slate-600">
        {formatDisplayDate(params.value)}
      </span>
    ),
  },
  {
    field: "action",
    headerName: "Actions",
    width: 130,
    headerAlign: "center",
    align: "center",
    sortable: false,
    renderCell: (params) => (
      <div className="flex items-center justify-center gap-1.5 w-full h-full">
        {params.row.fileUrl && (
          <Tooltip title="Download/View">
            <IconButton 
              component="a" 
              href={getFullUrl(params.row.fileUrl)} 
              target="_blank" 
              rel="noopener noreferrer"
              size="small"
              sx={{ color: "#134DA7", bgcolor: "#f1f5f9", "&:hover": { bgcolor: "#e2e8f0" } }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {activeTab === "myDocuments" && (
          <Tooltip title="Delete Document">
            <IconButton
              onClick={() => onDeleteDoc(params.row._id)}
              size="small"
              sx={{ color: "#ef4444", bgcolor: "#fef2f2", "&:hover": { bgcolor: "#fee2e2" } }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </div>
    ),
  },
];

const BookingDetails = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("myDocuments"); // "myDocuments", "expertDocuments", "chat"

  // File Upload state
  const [file, setFile] = useState(null);
  const [uploadNotes, setUploadNotes] = useState("");
  const [fileUploading, setFileUploading] = useState(false);
  const [sortModel, setSortModel] = useState([{ field: "createdAt", sort: "desc" }]);

  // Chat & Messaging states
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatFile, setChatFile] = useState(null);
  const [isDocRequest, setIsDocRequest] = useState(false);
  const [chatSending, setChatSending] = useState(false);

  const chatEndRef = useRef(null);
  const chatFileInputRef = useRef(null);

  // Reviews
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submittingReview, setSubmittingReview] = useState(false);
  const [hasReviewed, setHasReviewed] = useState(false);

  // Extra Charges Payment
  const [payingChargeId, setPayingChargeId] = useState(null);

  const token = localStorage.getItem("token") || sessionStorage.getItem("token");
  const userId = localStorage.getItem("userId");

  const scrollToChatBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchBooking = useCallback(async () => {
    try {
      setLoading(true);
      const [bookingRes, chatRes] = await Promise.all([
        axiosInstance.get(`/users/bookings/${bookingId}`),
        axiosInstance.get(`/chat/messages/${bookingId}`).catch(() => ({ data: { messages: [] } }))
      ]);

      const data = bookingRes.data.booking || bookingRes.data;
      setBooking(data);
      if (data.isReviewed) setHasReviewed(true);

      if (chatRes.data && chatRes.data.success) {
        setChatMessages(chatRes.data.messages || []);
      }
    } catch (err) {
      console.error("Fetch Error:", err);
      navigate("/profile/my-bookings");
    } finally {
      setLoading(false);
    }
  }, [bookingId, navigate]);

  useEffect(() => { fetchBooking(); }, [fetchBooking]);

  const appendUniqueMessage = (prevMessages, newMsg) => {
    if (!newMsg) return prevMessages;
    const isDuplicate = prevMessages.some((m) => {
      if (m._id && newMsg._id && m._id.toString() === newMsg._id.toString()) return true;
      if (
        m.text === newMsg.text &&
        m.senderType === newMsg.senderType &&
        Math.abs(new Date(m.createdAt || Date.now()) - new Date(newMsg.createdAt || Date.now())) < 3000
      ) {
        return true;
      }
      return false;
    });
    if (isDuplicate) return prevMessages;
    return [...prevMessages, newMsg];
  };

  // Socket for real-time messages & extra charges
  useEffect(() => {
    if (token && bookingId) {
      const socket = initializeSocket(token);
      if (socket) {
        socket.emit("join-chat", { bookingId });
        if (booking?._id) socket.emit("join-chat", { bookingId: booking._id });
        if (booking?.bookingId) socket.emit("join-chat", { bookingId: booking.bookingId });

        const handleNewMessage = (data) => {
          const isMatch =
            data.bookingId === bookingId ||
            data.bookingCode === bookingId ||
            data.bookingMongoId === bookingId ||
            (booking && (data.bookingId === booking._id || data.bookingCode === booking.bookingId));

          if (isMatch) {
            setChatMessages((prev) => appendUniqueMessage(prev, data.message));
            setTimeout(scrollToChatBottom, 100);
          }
        };

        const handleExtraChargeUpdate = (data) => {
          const isMatch =
            data?.bookingId === bookingId ||
            data?.bookingMongoId === bookingId ||
            data?.bookingCode === bookingId ||
            (booking && (data?.bookingId === booking._id || data?.bookingCode === booking.bookingId));

          if (isMatch) {
            fetchBooking();
          }
        };

        const handleStatusUpdate = (data) => {
          const isMatch =
            data?.bookingId === bookingId ||
            data?.bookingMongoId === bookingId ||
            data?.bookingCode === bookingId ||
            (booking && (data?.bookingId === booking._id || data?.bookingCode === booking.bookingId));

          if (isMatch) {
            if (data?.status) {
              setBooking((prev) => prev ? { ...prev, status: data.status } : prev);
            }
            fetchBooking();
          }
        };

        socket.on("new-chat-message", handleNewMessage);
        socket.on("extra-charge-updated", handleExtraChargeUpdate);
        socket.on("notification", handleExtraChargeUpdate);
        socket.on("booking:status_updated", handleStatusUpdate);

        return () => {
          socket.off("new-chat-message", handleNewMessage);
          socket.off("extra-charge-updated", handleExtraChargeUpdate);
          socket.off("notification", handleExtraChargeUpdate);
          socket.off("booking:status_updated", handleStatusUpdate);
        };
      }
    }
  }, [bookingId, token, booking, fetchBooking]);

  const handlePayExtraCharge = async (charge) => {
    if (!charge || !charge._id) return;
    const targetBookingId = booking?._id || bookingId;

    try {
      setPayingChargeId(charge._id);

      // 1. Create order on backend (routed to EasyMarg merchant account)
      const res = await axiosInstance.post(
        `/users/bookings/${targetBookingId}/extra-charges/${charge._id}/create-order`
      );

      if (!res.data.success) {
        throw new Error(res.data.message || "Failed to create payment order");
      }

      const { orderId, amount, currency, razorpayKeyId } = res.data;

      if (!window.Razorpay) {
        alert("Payment gateway is loading. Please refresh and try again.");
        setPayingChargeId(null);
        return;
      }

      const options = {
        key: razorpayKeyId,
        amount: amount,
        currency: currency || "INR",
        name: "EasyMarg",
        description: `Extra Charges: ${charge.reason}`,
        order_id: orderId,
        handler: async (paymentRes) => {
          try {
            // 2. Verify signature on EasyMarg backend
            const verifyRes = await axiosInstance.post(
              `/users/bookings/${targetBookingId}/extra-charges/${charge._id}/verify-payment`,
              {
                razorpay_order_id: paymentRes.razorpay_order_id,
                razorpay_payment_id: paymentRes.razorpay_payment_id,
                razorpay_signature: paymentRes.razorpay_signature,
              }
            );

            if (verifyRes.data.success) {
              alert("Payment successful! Your extra charges have been recorded with EasyMarg.");
              fetchBooking();
            } else {
              alert(verifyRes.data.message || "Payment verification failed.");
            }
          } catch (verifyErr) {
            console.error("Verification error:", verifyErr);
            alert(verifyErr.response?.data?.message || "Payment verification failed. Please contact EasyMarg support.");
          } finally {
            setPayingChargeId(null);
          }
        },
        prefill: {
          name: booking?.user?.name || "",
          email: booking?.user?.emailid || "",
          contact: booking?.user?.phoneno || "",
        },
        modal: {
          ondismiss: () => setPayingChargeId(null),
        },
        theme: { color: "#134DA7" },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      console.error("Initiate extra charge payment error:", err);
      alert(err.response?.data?.message || "Failed to initiate payment. Please try again.");
      setPayingChargeId(null);
    }
  };

  const handleUpload = async (e) => {
    if (e) e.preventDefault();
    if (!file && !uploadNotes.trim()) return;

    try {
      setFileUploading(true);
      const formData = new FormData();
      if (file) formData.append("file", file);
      if (uploadNotes.trim()) formData.append("notes", uploadNotes.trim());

      const response = await axiosInstance.post(`/users/bookings/${bookingId}/document-requests`, formData);

      if (response.data.success) {
        setFile(null);
        setUploadNotes("");
        fetchBooking();
      }
    } catch (err) {
      console.error("Upload Error:", err);
    } finally {
      setFileUploading(false);
    }
  };

  const handleDeleteUserDoc = async (docId) => {
    if (!window.confirm("Are you sure you want to delete this document?")) return;
    try {
      const res = await axiosInstance.delete(`/users/bookings/${bookingId}/documents/${docId}`);
      if (res.data.success) {
        fetchBooking();
      }
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  const handleSendChatMessage = async (e) => {
    e.preventDefault();
    if (!chatInput.trim() && !chatFile) return;

    try {
      setChatSending(true);
      const targetBookingId = booking?._id || booking?.bookingId || bookingId;
      const formData = new FormData();
      formData.append("bookingId", targetBookingId);
      formData.append("text", chatInput.trim());
      formData.append("isDocRequest", isDocRequest);

      if (chatFile) {
        formData.append("file", chatFile);
      }

      const res = await axiosInstance.post("/chat/send", formData);

      if (res.data.success) {
        setChatMessages((prev) => appendUniqueMessage(prev, res.data.message));
        setChatInput("");
        setChatFile(null);
        setIsDocRequest(false);
        if (chatFileInputRef.current) chatFileInputRef.current.value = "";
        setTimeout(scrollToChatBottom, 100);
      }
    } catch (err) {
      console.error("Failed to send chat message:", err);
    } finally {
      setChatSending(false);
    }
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (rating === 0) return alert("Please select a star rating.");

    try {
      setSubmittingReview(true);
      const res = await axiosInstance.post(`/users/reviews`, {
        bookingId: bookingId,
        rating,
        comment,
        vendorId: booking?.vendor?._id || booking?.vendorId?._id || booking?.vendorId || null
      });
      if (res.data.success) {
        setHasReviewed(true);
        alert("Thank you for your feedback!");
      }
    } catch (err) {
      if (err.response?.status === 400) setHasReviewed(true);
      alert(err.response?.data?.message || "Error submitting review");
    } finally {
      setSubmittingReview(false);
    }
  };

  const [joiningCall, setJoiningCall] = useState(false);

  const handleJoinCall = async () => {
    if (booking?.status?.toLowerCase() === "completed") {
      alert("This consultation has already been completed.");
      return;
    }
    setJoiningCall(true);
    try {
      const res = await axiosInstance.get(`/users/bookings/${bookingId}/video-token`);
      if (res.data?.token) {
        navigate(`/video/${bookingId}?token=${res.data.token}`);
      } else {
        alert("Unable to retrieve call token. Please try again.");
      }
    } catch (err) {
      alert(err.response?.data?.message || "Expert has not joined the call yet. Please try again in a moment.");
    } finally {
      setJoiningCall(false);
    }
  };

  if (loading || !booking) return (
    <div className="flex h-screen items-center justify-center bg-slate-50 text-blue-600 font-bold uppercase tracking-widest animate-pulse">
      Initializing Workspace...
    </div>
  );

  const expertName = booking?.vendor?.name 
    ? booking.vendor.name 
    : booking?.vendor?.firstName 
    ? `${booking.vendor.firstName} ${booking.vendor.lastName || ""}`.trim()
    : booking?.vendorId?.name 
    ? booking.vendorId.name 
    : booking?.vendorId?.firstName 
    ? `${booking.vendorId.firstName} ${booking.vendorId.lastName || ""}`.trim()
    : "Professional Expert";

  const extraCharges = booking?.extraCharges || [];
  const pendingCharges = extraCharges.filter((c) => c.status === "Pending");
  const totalPendingAmount = pendingCharges.reduce((sum, c) => sum + (Number(c.amount) || 0), 0);
  const paidCharges = extraCharges.filter((c) => c.status === "Paid");
  const totalPaidAmount = paidCharges.reduce((sum, c) => sum + (Number(c.amount) || 0), 0);

  let displayDocs = [];
  if (activeTab === "myDocuments") {
    displayDocs = booking?.userDocuments || [];
  } else if (activeTab === "expertDocuments") {
    const expertFiles = booking?.expertDocuments || booking?.documents || [];
    const requestedFiles = (booking?.documentRequests || [])
      .filter(d => d && (d.fileUrl || d.responseFileUrl))
      .map(d => ({
        _id: d._id,
        documentName: d.documentName || d.responseNotes || d.message || "Expert Document",
        fileUrl: d.responseFileUrl || d.fileUrl,
        notes: d.responseNotes || "",
        createdAt: d.respondedAt || d.createdAt
      }));
    displayDocs = [...expertFiles, ...requestedFiles];
  }

  const rows = displayDocs
    .filter(doc => (doc.fileUrl || doc.responseFileUrl || doc.documentName) && !doc.fileUrl?.includes("undefined"))
    .map((doc, i) => ({ 
      ...doc, 
      id: doc._id || i,
      _id: doc._id || i,
      fileUrl: doc.fileUrl || doc.responseFileUrl,
      documentName: doc.documentName || doc.message || "Document"
    }));

  const isCompleted = booking?.status?.toLowerCase() === "completed";
  const canJoin = booking?.status === "Accepted" || isCompleted;
  const isAudio = booking?.type?.includes("Voice") || booking?.type?.includes("Audio");

  return (
    <div className="min-h-screen bg-[#F8FAFC] relative font-outfit">
      
      {/* Dark Hero Background for Navbar Visibility & Premium Look */}
      <div className="absolute top-0 left-0 w-full h-[180px] bg-[#020617] z-0 overflow-hidden rounded-b-[2rem] shadow-xl">
         <div className="absolute inset-0 bg-gradient-to-b from-[#020617]/50 to-[#020617]"></div>
      </div>

      <div className="relative z-10 pt-24 pb-20 max-w-7xl w-full mx-auto px-4 md:px-6">
        
        {/* Breadcrumbs */}
        <div className="text-white text-xs font-bold uppercase tracking-widest mb-6 opacity-80 flex items-center gap-2">
            <span className="cursor-pointer hover:text-blue-300" onClick={() => navigate("/home")}>Home</span>
            <span>/</span>
            <span className="cursor-pointer hover:text-blue-300" onClick={() => navigate("/profile/my-bookings")}>My Bookings</span>
            <span>/</span>
            <span className="text-white opacity-100">Booking #{booking?.bookingId || "Workspace"}</span>
        </div>

        {/* Main Content White Canvas */}
        <div className="bg-white rounded-[2rem] shadow-xl border border-slate-100 p-5 md:p-8">
          
          <div className="w-full">
            {/* Unified Header */}
            <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-50 text-blue-600 shadow-sm">
                  <WorkIcon className="h-6 w-6" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-800">Booking Workspace</h2>
                  <p className="text-xs sm:text-sm text-gray-500">Manage documents and chat with your expert</p>
                </div>
              </div>

              {canJoin && (
                <button
                  type="button"
                  onClick={handleJoinCall}
                  disabled={joiningCall || isCompleted}
                  className={`flex items-center justify-center gap-2 px-5 py-2.5 font-bold text-xs uppercase tracking-wider rounded-xl transition-all ${
                    isCompleted
                      ? "bg-slate-100 text-slate-400 border border-slate-200 cursor-not-allowed shadow-none hover:bg-slate-100 opacity-60"
                      : "bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-600/20 active:scale-95 disabled:opacity-50"
                  }`}
                  title={isCompleted ? "Booking is completed. Call is closed." : "Join live call with expert"}
                >
                  {joiningCall ? (
                    <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : isAudio ? (
                    <PhoneIcon sx={{ fontSize: 16 }} />
                  ) : (
                    <VideoIcon sx={{ fontSize: 18 }} />
                  )}
                  <span>{joiningCall ? "Connecting..." : isCompleted ? "Completed" : "Join Live Call"}</span>
                </button>
              )}
            </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8">
          <div className="lg:col-span-4 space-y-6">
            <Paper className="p-6 rounded-2xl border border-slate-200 shadow-sm">
              <button onClick={() => navigate(-1)} className="flex items-center text-slate-500 hover:text-blue-600 mb-6 transition-colors">
                <BackIcon sx={{ fontSize: 18, mr: 1 }} />
                <span className="text-xs font-bold uppercase tracking-widest">Return to list</span>
              </button>
              <h2 className="text-2xl font-black text-slate-800 leading-tight">#{booking.bookingId}</h2>
              <Divider className="my-4" />
              <div className="space-y-4">
                <InfoItem icon={<ServiceIcon />} label="Service" value={booking.serviceTitle} />
                <InfoItem icon={<UserIcon />} label="Expert" value={expertName} />
                <InfoItem icon={<VideoIcon />} label="Type" value={booking.type} />
                <InfoItem icon={<TimeIcon />} label="Booked For" value={formatDisplayDate(booking.startTime)} />
              </div>

            </Paper>

            <AnimatePresence>
              {booking.status === "Completed" && !hasReviewed && (
                <motion.div 
                  initial={{ opacity: 0, y: 15 }} 
                  animate={{ opacity: 1, y: 0 }} 
                  exit={{ opacity: 0, scale: 0.9, height: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Paper className="p-6 rounded-2xl border-2 border-blue-100 bg-white shadow-lg shadow-blue-50">
                    <div className="flex items-center gap-2 mb-4">
                      <StarIcon className="text-blue-600" sx={{ fontSize: 20 }} />
                      <h3 className="text-sm font-black text-slate-800 uppercase tracking-tight">Rate the Expert</h3>
                    </div>
                    <form onSubmit={handleReviewSubmit} className="space-y-4">
                      <Box className="flex justify-center border p-2 rounded-xl bg-slate-50">
                        <Rating value={rating} onChange={(e, val) => setRating(val)} size="large" />
                      </Box>
                      <textarea
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        placeholder="Share your experience..."
                        className="w-full p-3 text-sm border border-slate-200 rounded-xl min-h-[100px] outline-none focus:ring-2 focus:ring-blue-100"
                      />
                      <button type="submit" disabled={submittingReview} className="w-full py-3 bg-blue-600 text-white rounded-xl font-bold text-xs uppercase hover:bg-blue-700 transition-all shadow-md shadow-blue-100">
                        {submittingReview ? "Submitting..." : "Submit Review"}
                      </button>
                    </form>
                  </Paper>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div className="lg:col-span-8 space-y-6">
            {/* PENDING EXTRA CHARGES BANNER */}
            {pendingCharges.length > 0 && (
              <div className="p-4 sm:p-5 rounded-2xl bg-amber-50 border border-amber-200 shadow-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 animate-in fade-in duration-300">
                <div className="flex items-start gap-3.5">
                  <div className="p-2.5 rounded-xl bg-amber-100 text-amber-800 shrink-0 mt-0.5">
                    <WarningIcon fontSize="small" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="font-bold text-amber-950 text-sm">
                        Extra Charges Requested ({pendingCharges.length})
                      </h4>
                      <span className="bg-amber-200/80 text-amber-900 text-[10px] font-black px-2 py-0.5 rounded-full">
                        ₹{totalPendingAmount.toLocaleString("en-IN")} Total
                      </span>
                    </div>
                    <p className="text-xs text-amber-800/90 mt-1 leading-relaxed">
                      {pendingCharges.map((c) => c.reason).join(", ")}. Payments are securely processed via EasyMarg.
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setActiveTab("extraCharges")}
                  className="px-5 py-2.5 bg-amber-600 hover:bg-amber-700 text-white font-bold text-xs uppercase tracking-wider rounded-xl shadow-md transition-all whitespace-nowrap active:scale-95 flex items-center gap-1.5 shrink-0"
                >
                  <PaymentIcon sx={{ fontSize: 16 }} /> Review & Pay
                </button>
              </div>
            )}

            {/* WORKSPACE TABS */}
            <div className="bg-white p-1 rounded-xl border border-slate-200 flex gap-1 shadow-sm overflow-x-auto no-scrollbar">
              <TabBtn active={activeTab === "myDocuments"} onClick={() => setActiveTab("myDocuments")} label="My Docs" />
              <TabBtn active={activeTab === "expertDocuments"} onClick={() => setActiveTab("expertDocuments")} label="Expert Docs" />
              <TabBtn 
                active={activeTab === "extraCharges"} 
                onClick={() => setActiveTab("extraCharges")} 
                label={`Extra Charges ${pendingCharges.length ? `(${pendingCharges.length} Pending)` : extraCharges.length ? `(${extraCharges.length})` : ""}`} 
                icon={<PaymentIcon sx={{ fontSize: 16, mr: 0.5 }} />} 
              />
              {activeTab === "chat" && (
                <TabBtn active={true} onClick={() => setActiveTab("chat")} label="Chat & Direct Messages" icon={<ChatIcon sx={{ fontSize: 16, mr: 0.5 }} />} />
              )}
            </div>

            {activeTab === "myDocuments" && (
              <Paper className="rounded-2xl border border-slate-200 shadow-sm overflow-hidden bg-white p-6 space-y-4">
                <span className="text-xs font-black text-slate-500 uppercase tracking-widest block">Upload Document & Note</span>
                <div className="flex flex-col gap-3">
                  <div className="flex flex-col sm:flex-row items-center gap-4">
                    <input id="file-upload" type="file" className="hidden" onChange={(e) => setFile(e.target.files[0])} />
                    <label htmlFor="file-upload" className="flex-1 h-20 border-2 border-dashed border-slate-200 rounded-2xl flex items-center justify-center gap-3 cursor-pointer hover:border-blue-300 hover:bg-blue-50/30 transition-all px-4">
                      <UploadIcon sx={{ fontSize: 26, color: file ? "#134DA7" : "#cbd5e1" }} />
                      <span className="text-xs font-bold text-slate-600 truncate">
                        {file ? file.name : "Click to select file (optional)"}
                      </span>
                    </label>
                  </div>
                  <textarea
                    value={uploadNotes}
                    onChange={(e) => setUploadNotes(e.target.value)}
                    placeholder="Add a note or instructions for the expert..."
                    className="w-full text-xs font-medium text-slate-700 bg-slate-50 border border-slate-200 rounded-xl p-3 outline-none resize-none focus:ring-2 focus:ring-blue-100"
                    rows="2"
                  />
                  <button 
                    onClick={handleUpload}
                    disabled={fileUploading || (!file && !uploadNotes.trim())} 
                    className={`w-full py-3 rounded-xl text-white font-bold text-xs uppercase tracking-widest transition-all ${fileUploading || (!file && !uploadNotes.trim()) ? "bg-slate-200 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-100"}`}
                  >
                    {fileUploading ? "Uploading..." : "Share with Expert"}
                  </button>
                </div>
              </Paper>
            )}

            {(activeTab === "myDocuments" || activeTab === "expertDocuments") && (
              <Paper className="rounded-2xl border border-slate-200 overflow-hidden bg-white shadow-sm">
                <Box sx={{ height: 450, width: "100%" }}>
                  <DataGrid
                    rows={rows}
                    columns={getColumns(activeTab, handleDeleteUserDoc)}
                    rowHeight={95}
                    getRowHeight={() => 'auto'}
                    disableRowSelectionOnClick
                    sortModel={sortModel}
                    onSortModelChange={setSortModel}
                    sx={{
                      border: "none",
                      "& .MuiDataGrid-columnHeaders": { bgcolor: "#f8fafc", color: "#64748b", fontSize: "11px", fontWeight: 900, textTransform: "uppercase" },
                      "& .MuiDataGrid-cell": { borderBottom: "1px solid #f1f5f9" }
                    }}
                  />
                </Box>
              </Paper>
            )}

            {activeTab === "extraCharges" && (
              <div className="space-y-6">
                {/* Header overview card */}
                <Paper className="p-6 rounded-2xl border border-slate-200 shadow-sm bg-gradient-to-r from-slate-900 to-blue-950 text-white">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-xl bg-white/10 text-amber-400">
                      <PaymentIcon fontSize="medium" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold">Booking Extra Charges</h3>
                      <p className="text-xs text-slate-300 mt-0.5">
                        Additional official costs, government fees, or stamp duties requested by your expert for this booking.
                      </p>
                    </div>
                  </div>
               
                </Paper>

                {/* Metric Summary */}
                {extraCharges.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="bg-amber-50/70 p-4 rounded-2xl border border-amber-200">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700">Pending Action</p>
                      <p className="text-xl font-black text-amber-800 mt-1">₹{totalPendingAmount.toLocaleString("en-IN")}</p>
                    </div>
                    <div className="bg-emerald-50/70 p-4 rounded-2xl border border-emerald-200">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">Paid to EasyMarg</p>
                      <p className="text-xl font-black text-emerald-800 mt-1">₹{totalPaidAmount.toLocaleString("en-IN")}</p>
                    </div>
                  </div>
                )}

                {/* Charges List */}
                {extraCharges.length === 0 ? (
                  <Paper className="p-12 text-center rounded-2xl border border-dashed border-slate-200 bg-white">
                    <ReceiptIcon sx={{ fontSize: 48, color: "#cbd5e1" }} className="mx-auto mb-2" />
                    <h4 className="text-sm font-bold text-slate-700">No Extra Charges</h4>
                    <p className="text-xs text-slate-400 mt-1 max-w-sm mx-auto">
                      Your expert has not requested any additional charges for this booking.
                    </p>
                  </Paper>
                ) : (
                  <div className="space-y-4">
                    {extraCharges.map((charge) => {
                      const isPending = charge.status === "Pending";
                      const isPaid = charge.status === "Paid";
                      const isCancelled = charge.status === "Cancelled";
                      const isPaying = payingChargeId === charge._id;

                      return (
                        <Paper
                          key={charge._id}
                          className={`p-6 rounded-2xl border shadow-sm transition-all ${
                            isPending
                              ? "border-amber-200 bg-amber-50/20"
                              : isPaid
                              ? "border-emerald-200 bg-emerald-50/20"
                              : "border-slate-200 bg-slate-50/40"
                          }`}
                        >
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                            <div className="space-y-1.5 flex-1">
                              <div className="flex items-center gap-2">
                                <h4 className="font-bold text-slate-900 text-base">{charge.reason}</h4>
                                {isPending && (
                                  <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase bg-amber-100 text-amber-800 border border-amber-200">
                                    Action Required
                                  </span>
                                )}
                                {isPaid && (
                                  <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase bg-emerald-100 text-emerald-800 border border-emerald-200 flex items-center gap-1">
                                    <CheckCircleIcon sx={{ fontSize: 12 }} /> Paid 
                                  </span>
                                )}
                                {isCancelled && (
                                  <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase bg-slate-100 text-slate-500">
                                    Cancelled
                                  </span>
                                )}
                              </div>

                              {charge.description && (
                                <p className="text-xs text-slate-600 leading-relaxed whitespace-pre-line">
                                  {charge.description}
                                </p>
                              )}

                              <div className="flex flex-wrap items-center gap-4 text-[11px] text-slate-400 pt-1">
                                <span>Requested: {charge.requestedAt ? new Date(charge.requestedAt).toLocaleDateString("en-GB") : "—"}</span>
                                {isPaid && charge.paidAt && (
                                  <span className="text-emerald-700 font-semibold">
                                    Paid on: {new Date(charge.paidAt).toLocaleDateString("en-GB")}
                                  </span>
                                )}
                                {isPaid && charge.payment?.paymentId && (
                                  <span className="font-mono text-[10px] text-slate-500">
                                    Txn ID: {charge.payment.paymentId}
                                  </span>
                                )}
                              </div>
                            </div>

                            <div className="flex flex-col sm:items-end gap-2 shrink-0 border-t sm:border-t-0 pt-3 sm:pt-0 border-slate-100">
                              <span className="text-2xl font-black text-slate-900">
                                ₹{Number(charge.amount).toLocaleString("en-IN")}
                              </span>

                              {isPending && (
                                <button
                                  onClick={() => handlePayExtraCharge(charge)}
                                  disabled={isPaying}
                                  className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-bold text-xs uppercase tracking-wider rounded-xl shadow-lg shadow-blue-100 transition-all flex items-center gap-2 active:scale-95 whitespace-nowrap cursor-pointer"
                                >
                                  <PaymentIcon sx={{ fontSize: 16 }} />
                                  {isPaying ? "Processing..." : `Pay ₹${Number(charge.amount).toLocaleString("en-IN")} Now`}
                                </button>
                              )}
                            </div>
                          </div>
                        </Paper>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {activeTab === "chat" && (
              <Paper className="p-4 rounded-2xl border border-slate-200 shadow-sm flex flex-col h-[520px] bg-white">
                {/* Chat Message Window */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50 rounded-xl border border-slate-200 mb-3">
                  {chatMessages.length === 0 ? (
                    <div className="text-center py-20 text-slate-400 text-xs italic">
                      No chat history yet. Ask expert regarding docs or send a direct message below.
                    </div>
                  ) : (
                    chatMessages.map((m, idx) => {
                      const isMe = m.senderType === "user" || m.senderId === userId;
                      return (
                        <div key={m._id || idx} className={`flex flex-col ${isMe ? "items-end" : "items-start"}`}>
                          <div
                            className={`max-w-[85%] sm:max-w-[75%] p-3 rounded-2xl text-[11px] shadow-xs ${
                              isMe
                                ? "bg-blue-600 text-white rounded-br-none"
                                : "bg-white text-slate-800 border border-slate-200 rounded-bl-none"
                            }`}
                          >
                            <div className={`text-[10px] font-bold mb-1 ${isMe ? "text-blue-200" : "text-blue-600"}`}>
                              {isMe ? "You" : expertName}
                            </div>

                            {m.isDocRequest && (
                              <div className={`text-[11px] font-semibold px-2 py-0.5 rounded mb-1 flex items-center gap-1 ${
                                isMe ? "bg-blue-700 text-blue-100" : "bg-amber-50 text-amber-800 border border-amber-200"
                              }`}>
                                <DocIcon style={{ fontSize: 14 }} /> Document Request
                              </div>
                            )}

                            {m.text && <p className="whitespace-pre-wrap break-words leading-relaxed text-[11px] md:text-xs font-medium">{m.text}</p>}

                            {m.fileUrl && (
                              <div className={`mt-2 p-2 rounded-xl border flex items-center justify-between gap-2 ${
                                isMe ? "bg-blue-700/60 border-blue-500/50 text-white" : "bg-slate-50 border-slate-200 text-slate-800"
                              }`}>
                                <div className="truncate text-xs">
                                  <p className="font-bold truncate">{m.fileName || "Attached File"}</p>
                                </div>
                                <a
                                  href={getFullUrl(m.fileUrl)}
                                  target="_blank"
                                  rel="noreferrer"
                                  className={`p-1.5 rounded-lg text-xs flex items-center justify-center ${
                                    isMe ? "bg-white text-blue-600 hover:bg-blue-50" : "bg-blue-600 text-white hover:bg-blue-700"
                                  }`}
                                  title="View / Download File"
                                >
                                  <VisibilityIcon style={{ fontSize: 14 }} />
                                </a>
                              </div>
                            )}

                            <div className={`text-[9px] text-right mt-1 opacity-75 ${isMe ? "text-blue-100" : "text-slate-400"}`}>
                              {new Date(m.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div ref={chatEndRef} />
                </div>

                {/* Direct Message Input Bar */}
                <form onSubmit={handleSendChatMessage} className="flex flex-col gap-2">
                  {chatFile && (
                    <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 px-3 py-1 rounded-lg text-xs text-blue-700 w-fit">
                      <AttachFileIcon style={{ fontSize: 14 }} />
                      <span className="font-semibold truncate max-w-[200px]">{chatFile.name}</span>
                      <button type="button" onClick={() => setChatFile(null)} className="text-blue-500 hover:text-red-600 font-bold ml-1">
                        ✕
                      </button>
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    <input
                      type="file"
                      ref={chatFileInputRef}
                      accept="*"
                      onChange={(e) => setChatFile(e.target.files[0])}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => chatFileInputRef.current?.click()}
                      className="p-2 text-slate-500 hover:text-blue-600 border border-slate-200 rounded-lg"
                      title="Attach file (Excel, TXT, PDF, DOCX, ZIP, etc.)"
                    >
                      <AttachFileIcon fontSize="small" />
                    </button>

                    <input
                      type="text"
                      placeholder="Ask expert regarding docs or send direct message..."
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white"
                    />

                    <label className="flex items-center gap-1 text-[11px] text-slate-600 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={isDocRequest}
                        onChange={(e) => setIsDocRequest(e.target.checked)}
                        className="rounded text-blue-600"
                      />
                      Request Doc
                    </label>

                    <button
                      type="submit"
                      disabled={chatSending || (!chatInput.trim() && !chatFile)}
                      className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg font-bold text-xs uppercase flex items-center gap-1"
                    >
                      <SendIcon style={{ fontSize: 14 }} /> {chatSending ? "..." : "Send"}
                    </button>
                  </div>
                </form>
              </Paper>
            )}
          </div>
        </div>
      </div>
      </div>
      </div>

      {/* FLOATING CHAT ACTION BUTTON (FAB) IN BOTTOM-RIGHT CORNER */}
      <Tooltip title="Chat & Direct Messages with Expert" placement="left">
        <button
          onClick={() => {
            setActiveTab(prev => prev === "chat" ? "myDocuments" : "chat");
            setTimeout(scrollToChatBottom, 100);
          }}
          className="fixed bottom-6 right-6 z-[999] w-14 h-14 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-2xl flex items-center justify-center transition-all hover:scale-110 active:scale-95 border-2 border-white"
        >
          <ChatIcon sx={{ fontSize: 26 }} />
        </button>
      </Tooltip>
      
    </div>
  );
};

const InfoItem = ({ icon, label, value }) => (
  <div className="flex items-center gap-4">
    <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center text-slate-400">
      {React.cloneElement(icon, { sx: { fontSize: 20 } })}
    </div>
    <div>
      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{label}</p>
      <p className="text-sm font-bold text-slate-700">{value || "N/A"}</p>
    </div>
  </div>
);

const TabBtn = ({ active, onClick, label, icon }) => (
  <button onClick={onClick} className={`flex-1 py-3 rounded-lg text-xs font-bold uppercase tracking-wide transition-all flex items-center justify-center gap-1 ${active ? "bg-blue-600 text-white shadow-md shadow-blue-100" : "text-slate-500 hover:bg-slate-50"}`}>
    {icon} {label}
  </button>
);

export default BookingDetails;